// =====================================================
// Fingerprint Service - Add as: _services/fingerprint.service.ts
// =====================================================
//
// INSTRUCTIONS:
// 1. Add this file to your _services folder
// 2. Import in your authentication.service.ts
// 3. Use in login flow to collect device fingerprint
//

import { Injectable } from '@angular/core';

export interface FingerprintComponents {
  userAgent: string;
  language: string;
  platform: string;
  timezone: string;
  timezoneOffset: number;
  screenResolution: string;
  colorDepth: number;
  hardwareConcurrency: number;
  deviceMemory: number | null;
  maxTouchPoints: number;
  plugins: string;
  canvas: string;
  webgl: string;
  webglVendor: string;
  fonts: string;
  audio: string;
  storage: string;
  cookiesEnabled: boolean;
  doNotTrack: string | null;
}

export interface DeviceFingerprint {
  hash: string;
  components: FingerprintComponents;
}

@Injectable({
  providedIn: 'root'
})
export class FingerprintService {
  private cachedFingerprint: DeviceFingerprint | null = null;
  private cacheExpiry: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor() {}

  /**
   * Get device fingerprint (cached for performance)
   */
  async getFingerprint(): Promise<DeviceFingerprint> {
    const now = Date.now();
    
    if (this.cachedFingerprint && now < this.cacheExpiry) {
      return this.cachedFingerprint;
    }

    const components = await this.collectComponents();
    const hash = await this.generateHash(components);

    this.cachedFingerprint = { hash, components };
    this.cacheExpiry = now + this.CACHE_DURATION;

    return this.cachedFingerprint;
  }

  /**
   * Clear cached fingerprint (call on logout)
   */
  clearCache(): void {
    this.cachedFingerprint = null;
    this.cacheExpiry = 0;
  }

  /**
   * Collect all fingerprint components
   */
  private async collectComponents(): Promise<FingerprintComponents> {
    const [canvas, webgl, audio, fonts] = await Promise.all([
      this.getCanvasFingerprint(),
      this.getWebGLFingerprint(),
      this.getAudioFingerprint(),
      this.getFonts()
    ]);

    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      screenResolution: `${screen.width}x${screen.height}`,
      colorDepth: screen.colorDepth,
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      deviceMemory: (navigator as any).deviceMemory || null,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      plugins: this.getPlugins(),
      canvas,
      webgl: webgl.renderer,
      webglVendor: webgl.vendor,
      fonts,
      audio,
      storage: this.getStorageCapabilities(),
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack
    };
  }

  /**
   * Generate SHA-256 hash from components
   */
  private async generateHash(components: FingerprintComponents): Promise<string> {
    const sortedKeys = Object.keys(components).sort();
    const values = sortedKeys.map(key => {
      const value = (components as any)[key];
      return value !== null && value !== undefined ? String(value) : '';
    });
    
    const data = values.join('|');
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Get browser plugins
   */
  private getPlugins(): string {
    if (!navigator.plugins || navigator.plugins.length === 0) {
      return '';
    }
    
    const plugins: string[] = [];
    for (let i = 0; i < Math.min(navigator.plugins.length, 10); i++) {
      plugins.push(navigator.plugins[i].name);
    }
    
    return plugins.sort().join(',');
  }

  /**
   * Get canvas fingerprint
   */
  private getCanvasFingerprint(): Promise<string> {
    return new Promise((resolve) => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 50;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve('');
          return;
        }

        // Draw various elements
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillStyle = '#f60';
        ctx.fillRect(10, 1, 62, 20);
        
        ctx.fillStyle = '#069';
        ctx.fillText('Hello, World!', 2, 15);
        
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('Canvas FP', 4, 17);

        // Get data URL and hash it
        const dataUrl = canvas.toDataURL();
        this.hashString(dataUrl).then(resolve);
      } catch {
        resolve('');
      }
    });
  }

  /**
   * Get WebGL fingerprint
   */
  private getWebGLFingerprint(): Promise<{ renderer: string; vendor: string }> {
    return new Promise((resolve) => {
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        
        if (!gl) {
          resolve({ renderer: '', vendor: '' });
          return;
        }

        const glContext = gl as WebGLRenderingContext;
        const debugInfo = glContext.getExtension('WEBGL_debug_renderer_info');
        
        if (debugInfo) {
          resolve({
            renderer: glContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '',
            vendor: glContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || ''
          });
        } else {
          resolve({
            renderer: glContext.getParameter(glContext.RENDERER) || '',
            vendor: glContext.getParameter(glContext.VENDOR) || ''
          });
        }
      } catch {
        resolve({ renderer: '', vendor: '' });
      }
    });
  }

  /**
   * Get audio fingerprint
   */
  private getAudioFingerprint(): Promise<string> {
    return new Promise((resolve) => {
      try {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContext) {
          resolve('');
          return;
        }

        const context = new AudioContext();
        const oscillator = context.createOscillator();
        const analyser = context.createAnalyser();
        const gain = context.createGain();
        const processor = context.createScriptProcessor(4096, 1, 1);

        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(10000, context.currentTime);

        gain.gain.setValueAtTime(0, context.currentTime);

        oscillator.connect(analyser);
        analyser.connect(processor);
        processor.connect(gain);
        gain.connect(context.destination);

        let fingerprint = '';
        processor.onaudioprocess = (event) => {
          const data = event.inputBuffer.getChannelData(0);
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            sum += Math.abs(data[i]);
          }
          fingerprint = sum.toString();
          
          // Cleanup
          oscillator.disconnect();
          analyser.disconnect();
          processor.disconnect();
          gain.disconnect();
          context.close();
          
          resolve(fingerprint);
        };

        oscillator.start(0);
        
        // Timeout fallback
        setTimeout(() => {
          if (!fingerprint) {
            resolve('');
          }
        }, 500);
      } catch {
        resolve('');
      }
    });
  }

  /**
   * Get list of installed fonts
   */
  private getFonts(): Promise<string> {
    return new Promise((resolve) => {
      const testFonts = [
        'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 
        'Verdana', 'Courier New', 'Comic Sans MS', 'Impact',
        'Trebuchet MS', 'Arial Black', 'Lucida Console',
        'Palatino Linotype', 'Tahoma', 'Century Gothic'
      ];

      const detected: string[] = [];
      const baseFonts = ['monospace', 'sans-serif', 'serif'];
      const testString = 'mmmmmmmmmmlli';
      const testSize = '72px';

      const span = document.createElement('span');
      span.style.position = 'absolute';
      span.style.left = '-9999px';
      span.style.fontSize = testSize;
      span.innerText = testString;
      document.body.appendChild(span);

      const baseWidths: { [key: string]: number } = {};
      baseFonts.forEach(baseFont => {
        span.style.fontFamily = baseFont;
        baseWidths[baseFont] = span.offsetWidth;
      });

      testFonts.forEach(font => {
        let isDetected = false;
        for (const baseFont of baseFonts) {
          span.style.fontFamily = `'${font}', ${baseFont}`;
          if (span.offsetWidth !== baseWidths[baseFont]) {
            isDetected = true;
            break;
          }
        }
        if (isDetected) {
          detected.push(font);
        }
      });

      document.body.removeChild(span);
      resolve(detected.sort().join(','));
    });
  }

  /**
   * Get storage capabilities
   */
  private getStorageCapabilities(): string {
    const capabilities: string[] = [];
    
    try {
      if (window.localStorage) capabilities.push('localStorage');
    } catch {}
    
    try {
      if (window.sessionStorage) capabilities.push('sessionStorage');
    } catch {}
    
    try {
      if (window.indexedDB) capabilities.push('indexedDB');
    } catch {}

    return capabilities.join(',');
  }

  /**
   * Hash a string using SHA-256
   */
  private async hashString(str: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 16);
  }
}
